import React, { useCallback } from 'react';
import { saveAs } from 'file-saver';
import { Resources } from '@erecruit/types';
import { Intl } from '@erecruit/core';
import {
  EmptyState,
  Flex,
  H1,
  H3,
  Icon,
  Modal,
  ModalProps,
  Spinner,
  styled,
  Tag,
} from '@erecruit/ui-kit';
import { useMedia } from '@erecruit/modules';

const { FormattedMessage, injectIntl } = Intl;

interface ResumeModalProps extends ModalProps, Intl.InjectedIntlProps {
  resume: Resources.CandidateResume;
  name: string;
}

const FrameWrapper = styled(Flex)({
  minHeight: '70%',
});
const TagLink = styled(Tag)({
  cursor: 'pointer',
});

const ResumeModal = ({
  intl,
  resume: { resume },
  name,
  ...modalProps
}: ResumeModalProps) => {
  const { blob, dataURL, loading } = useMedia(resume?.id);

  const handleDownload = useCallback(() => {
    if (blob) {
      saveAs(blob, resume.name);
    }
  }, [blob, resume]);

  return (
    <Modal
      width="70%"
      bodyStyle={{
        height: '80vh',
        padding: 0,
      }}
      cancelText={false}
      footer={null}
      pageTitle={
        <div>
          <H1 color="headerTertiary" align="center">
            {intl.formatMessage({
              id: messageIds.vms.matchRecord.resume.defaultResume,
            })}
          </H1>
          <H3 color="headerTertiary" align="center">
            {name}
          </H3>
        </div>
      }
      {...modalProps}
    >
      <Flex padding={6} justify="flex-end">
        {blob ? (
          <TagLink circle onClick={handleDownload}>
            <Icon type="download" color="bodyTertiary" size="small" />
          </TagLink>
        ) : null}
      </Flex>
      {loading ? (
        <Flex align="center" justify="center" stretch>
          <Spinner
            tip={intl.formatMessage({
              id: messageIds.vms.matchRecord.loadingAttachment,
            })}
          />
        </Flex>
      ) : (
        <FrameWrapper align="center" justify="center" stretch>
          {dataURL ? (
            <iframe
              frameBorder={0}
              title={resume.name}
              height="100%"
              width="100%"
              src={dataURL}
            ></iframe>
          ) : (
            <EmptyState
              description={
                <FormattedMessage
                  id={messageIds.vms.matchRecord.previewNotSupported}
                />
              }
            />
          )}
        </FrameWrapper>
      )}
    </Modal>
  );
};

export default injectIntl(ResumeModal);
